import { Alert, AlertDescription, AlertTitle, Box, Button, Center, Table, Tbody, Td, Text, Th, Thead, Tr, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useParams } from 'react-router-dom';
import { fetchSuppliesByCategory, reorderSupplies } from '../../api';
import AccessDenied from '../../components/AccessDenied';
import Loading from '../../components/Loading/Loading';
import TitleHeader from '../../components/TitleHeader';
import { useTableColors } from '../../Utils';

const CategorySuppliesPage = () => {
    const { categoryId } = useParams();
    const [supplies, setSupplies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [catName, setCatName] = useState("");
    const coolors = useTableColors();

    useEffect(() => {
        loadSupplies();
    }, [categoryId]);

    const loadSupplies = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await fetchSuppliesByCategory(categoryId);
            console.log(result)
            if (result.success) {
                setSupplies(result.data.supplies);
                setCatName(result.data.categoryName);
            } else {
                setError(result.message || 'Failed to fetch supplies');
            }
        } catch (err) {
            setError('Server error');
        } finally {
            setLoading(false);
        }
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(supplies);
        const [reordered] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reordered);
        setSupplies(items);
        setHasChanges(true);
    };

    const handleSaveOrder = async () => {
        try {
            const payload = supplies.map((item, index) => ({
                _id: item._id,
                order: index + 1,
            }));
            const result = await reorderSupplies(payload);
            if (result.success) {
                setHasChanges(false);
            } else {
                console.error('Save failed:', result.message);
            }
        } catch (err) {
            console.error('Server error:', err);
        }
    };

    if (loading) {
        return (
            <Center mt="20%">
                <Loading />
            </Center>
        );
    }

    if (error) {
        return <AccessDenied msg={error} />;
    }

    return (
        <Box maxW="full" mx="auto" p="8">
            <VStack spacing={6} align="stretch">
                <TitleHeader title={`Supplies for ( ${catName || ""} )`} />

                {hasChanges && (
                    <Alert
                        status="info"
                        variant="subtle"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                        height="140px"
                        borderRadius={20}
                    >
                        <AlertTitle mb={1} fontSize="lg">
                            Supplies order changed
                        </AlertTitle>
                        <Text>You have to save in order for these changes to apply</Text>
                        <AlertDescription>
                            <Button
                                isLoading={false}
                                loadingText={"Saving Order"}
                                mt={4}
                                size="sm"
                                colorScheme="red"
                                onClick={handleSaveOrder}
                            >
                                Save Order
                            </Button>
                        </AlertDescription>
                    </Alert>
                )}


                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="suppliesList">
                        {(provided) => (
                            <Table
                                size="md"
                                variant="simple"
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                            >
                                <Thead bg={coolors.theadBg}>
                                    <Tr>
                                        <Th>#</Th>
                                        <Th>Name</Th>
                                        <Th>Size</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {supplies.map((supply, index) => (
                                        <Draggable
                                            key={supply._id}
                                            draggableId={supply._id}
                                            index={index}
                                        >
                                            {(provided, snapshot) => (
                                                <Tr
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    bg={
                                                        snapshot.isDragging
                                                            ? 'blue.100'
                                                            : index % 2 === 0
                                                                ? coolors.rowBgEven
                                                                : coolors.rowBgOdd
                                                    }
                                                    _hover={{ bg: coolors.rowHoverBg, color: 'white' }}
                                                >
                                                    <Td>{index + 1}</Td>
                                                    <Td>{supply.name}</Td>
                                                    <Td>{supply.size}</Td>
                                                </Tr>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </Tbody>
                            </Table>
                        )}
                    </Droppable>
                </DragDropContext>


            </VStack>
        </Box>
    );
};

export default CategorySuppliesPage;
